import { createSelector } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { nanoid } from 'nanoid';
import { depositTransaction, loanTransaction } from '@monestry-dev/schema';
import { applyFIFOReturnAll } from '../../../../misc/arrayHelpers';
import * as globals from '..';
import getSimulatedRepaymentSchedule from './getSimulatedRepaymentSchedule';
import i18n from '../../../../i18n';

dayjs.extend(utc);

function parseTags(tags) {
  return typeof tags === 'string' ? JSON.parse(tags) : tags || {};
}

function getTransactionSign(transaction) {
  const numericValue = [transaction?.quantity, transaction?.accountCurrencyAmount, transaction?.amount]
    .find((value) => typeof value === 'number' && value !== 0);
  return numericValue && numericValue < 0 ? -1 : 1;
}

function buildDerivedId(sourceId, suffix) {
  return `${sourceId}:${suffix}`;
}

function getSourceUpbc(transaction, baseCurrency, currency, quotes) {
  if (Number.isFinite(Number(transaction?.upbc)) && Number(transaction.upbc) !== 0) return Number(transaction.upbc);
  if (currency === baseCurrency) return 1;

  const currentQuoteBaseCurrency = quotes?.[currency]?.current?.quoteBaseCurrency;
  if (currentQuoteBaseCurrency) return 1 / currentQuoteBaseCurrency;
  return 1;
}

function getOpeningTransactionDate(account) {
  const firstInterestRateDate = (account?.interestRate || [])
    .map((item) => item?.effectiveDate)
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b)[0];

  return firstInterestRateDate || dayjs.utc().startOf('day').valueOf();
}

function createLoanSideTransaction({
  id,
  account,
  date,
  quantity,
  quantityInterest = 0,
  description,
  projectId = null,
  upac = 1,
  upbc = 1,
  uptc = 1,
  isSimulated = false,
  linkedTransactions = [],
  tags = {},
}) {
  return loanTransaction.cast({
    id,
    accountId: account.id,
    date,
    category: 'loans',
    description,
    projectId,
    quantity,
    quantityInterest,
    upac,
    upbc,
    uptc,
    accountCurrency: account.currency,
    assetCurrency: account.currency,
    assetId: account.currency,
    transactionCurrency: account.currency,
    isSimulated,
    linkedTransactions,
    tags,
  });
}

function createRepaymentTransactionFromDeposit(transaction, account, baseCurrency, quotes) {
  const tags = parseTags(transaction.tags);
  const principal = Math.abs(Number(tags?.loanRepayment?.principal) || 0);
  const interest = Math.abs(Number(tags?.loanRepayment?.interest) || 0);

  if (!principal && !interest) return null;

  const signMultiplier = getTransactionSign(transaction) * -1;

  return createLoanSideTransaction({
    id: buildDerivedId(transaction.id, 'loan-repayment'),
    account,
    date: transaction.date,
    quantity: signMultiplier * principal,
    quantityInterest: signMultiplier * interest,
    description: transaction.description,
    projectId: transaction.projectId,
    upac: Number(transaction.upac) || 1,
    upbc: getSourceUpbc(transaction, baseCurrency, account.currency, quotes),
    uptc: Number(transaction.uptc) || 1,
    isSimulated: !!transaction.isSimulated,
    linkedTransactions: [{
      id: transaction.id,
      category: 'deposits',
      tags: {
        type: 'transfer',
      },
    }],
    tags: {
      sourceDepositTransactionId: transaction.id,
    },
  });
}

function createPayoutTransactionFromDeposit(transaction, account, baseCurrency, quotes) {
  const quantityAbsolute = Math.abs(Number(transaction.accountCurrencyAmount ?? transaction.quantity) || 0);
  if (!quantityAbsolute) return null;

  return createLoanSideTransaction({
    id: buildDerivedId(transaction.id, 'loan-payout'),
    account,
    date: transaction.date,
    quantity: getTransactionSign(transaction) * -quantityAbsolute,
    quantityInterest: 0,
    description: transaction.description,
    projectId: transaction.projectId,
    upac: Number(transaction.upac) || 1,
    upbc: getSourceUpbc(transaction, baseCurrency, account.currency, quotes),
    uptc: Number(transaction.uptc) || 1,
    isSimulated: !!transaction.isSimulated,
    linkedTransactions: [{
      id: transaction.id,
      category: 'deposits',
      tags: {
        type: 'transfer',
      },
    }],
    tags: {
      sourceDepositTransactionId: transaction.id,
    },
  });
}

function createSyntheticOpeningTransaction(account, baseCurrency, quotes) {
  const quantity = Number(account?.loanAmount);
  if (!Number.isFinite(quantity) || quantity === 0) return null;

  return createLoanSideTransaction({
    id: buildDerivedId(account.id, 'loan-opening'),
    account,
    date: getOpeningTransactionDate(account),
    quantity,
    quantityInterest: 0,
    description: i18n.language === 'de' ? 'Anfangssaldo' : 'Opening balance',
    projectId: account.connectedProjectId || null,
    upac: 1,
    upbc: getSourceUpbc(null, baseCurrency, account.currency, quotes),
    uptc: 1,
    tags: {
      syntheticOpening: true,
      nonEditable: true,
    },
  });
}

function deriveRealizedLoanTransactions(depositTransactions, accounts, baseCurrency, quotes) {
  const accountById = Object.fromEntries((accounts || []).map((account) => [account.id, account]));
  const payoutTransactionCount = new Map();
  const realizedTransactions = [];

  (depositTransactions || []).forEach((transaction) => {
    const tags = parseTags(transaction.tags);
    const targetAccountId = tags?.accountId;
    const account = accountById[targetAccountId];

    if (!account) return;

    if (transaction.label === 'investment-payout') {
      const payoutTransaction = createPayoutTransactionFromDeposit(transaction, account, baseCurrency, quotes);
      if (payoutTransaction) {
        payoutTransactionCount.set(account.id, (payoutTransactionCount.get(account.id) || 0) + 1);
        realizedTransactions.push(payoutTransaction);
      }
      return;
    }

    if (transaction.label !== 'investment-loanRepayment' || !tags?.loanRepayment) return;

    const repaymentTransaction = createRepaymentTransactionFromDeposit(transaction, account, baseCurrency, quotes);
    if (repaymentTransaction) realizedTransactions.push(repaymentTransaction);
  });

  (accounts || []).forEach((account) => {
    if ((payoutTransactionCount.get(account.id) || 0) > 0) return;
    const openingTransaction = createSyntheticOpeningTransaction(account, baseCurrency, quotes);
    if (openingTransaction) realizedTransactions.push(openingTransaction);
  });

  return realizedTransactions.sort((a, b) => a.date - b.date);
}

function applyLoanTransactionTypes(transactions, accounts) {
  return (transactions || []).map((txn) => {
    const account = accounts.find((acc) => acc.id === txn.accountId);
    if (!account) return txn;

    switch (account.direction) {
      case 'received':
        return { ...txn, transactionType: txn.quantity >= 0 ? 'sale' : 'purchase' };
      case 'granted':
        return { ...txn, transactionType: txn.quantity < 0 ? 'sale' : 'purchase' };
      default:
        console.error('Unknown account direction', account.direction);
        return txn;
    }
  });
}

function getScheduleStartDate(realizedTransactions) {
  const lastTransactionDate = realizedTransactions.slice().sort((a, b) => b.date - a.date)[0]?.date;
  return dayjs.utc(lastTransactionDate || dayjs.utc().valueOf()).add(1, 'month').startOf('month').valueOf();
}

// DOES NOT return user-added out of schedule transactions (e.g. manual principal payment)
function transformRepaymentScheduleItemToTransaction(item, accountId, projectId, currency, baseCurrency, quotes, transactions) {
  let upbc = 1;
  if (baseCurrency !== currency) {
    const currentQBC = quotes[currency]?.current?.quoteBaseCurrency;
    upbc = (currentQBC && currentQBC !== 0 ? (1 / currentQBC) : undefined);
    if (!upbc) {
      const lastTransaction = transactions
        .filter((transaction) => transaction.accountId === accountId)
        .sort((a, b) => b.date - a.date)[0];
      upbc = lastTransaction?.upbc;
    }
    if (!upbc) {
      throw new Error('loansTransactions > transformRepaymentScheduleItemToTransaction: no upbc found');
    }
  }

  const linkedDepositTransctionId = nanoid();
  const commonProperties = loanTransaction.cast({
    id: nanoid(),
    accountId,
    projectId,
    date: item.date,
    category: 'loans',
    description: `${i18n.language === 'de' ? 'Darlehensrate' : 'Installment'} ${dayjs.utc(item.date).format('YYYY-MM')}`,
    assetId: currency,
    accountCurrency: currency,
    assetCurrency: currency,
    upbc,
    upac: 1,
    uptc: 1,
    isSimulated: true,
    linkedTransactions: [
      {
        id: linkedDepositTransctionId,
        category: 'deposits',
        tags: {
          type: 'transfer',
        },
      },
    ],
    tags: {
      linkedDepositTransctionId,
    },
  });

  return [
    {
      ...commonProperties,
      quantity: item.currentPrincipal,
      quantityInterest: item.currentInterest,
    },
  ];
}

function buildLoanTransactions({
  depositTransactions,
  explicitSimulatedLoanTransactions,
  accounts,
  quotes,
  baseCurrency,
  baseDate = null,
}) {
  const realizedTransactions = deriveRealizedLoanTransactions(depositTransactions, accounts, baseCurrency, quotes);

  const transactionsWithSimulated = accounts.flatMap((loanAccount) => {
    const thisAccountRealizedTransactions = realizedTransactions.filter((txn) => txn.accountId === loanAccount.id);
    const thisAccountExplicitSimulatedTransactions = explicitSimulatedLoanTransactions.filter((txn) => txn.accountId === loanAccount.id);

    const outstandingPrincipal = thisAccountRealizedTransactions.reduce((acc, transaction) => acc + transaction.quantity, 0);
    const repaymentSchedule = getSimulatedRepaymentSchedule(
      getScheduleStartDate(thisAccountRealizedTransactions),
      outstandingPrincipal,
      loanAccount,
      thisAccountExplicitSimulatedTransactions,
    ) || { schedule: [] };

    const simulatedRepaymentTransactionsForAccount = repaymentSchedule.schedule
      .filter((item) => (baseDate ? item.date.valueOf() <= baseDate : true))
      .flatMap((item) => transformRepaymentScheduleItemToTransaction(
        item,
        loanAccount.id,
        loanAccount.connectedProjectId,
        loanAccount.currency,
        baseCurrency,
        quotes,
        thisAccountRealizedTransactions,
      ));

    const reverseMode = loanAccount.direction === 'received';
    const allTransactions = thisAccountRealizedTransactions.concat(thisAccountExplicitSimulatedTransactions).concat(simulatedRepaymentTransactionsForAccount);
    return applyFIFOReturnAll(allTransactions, reverseMode);
  });

  return applyLoanTransactionTypes(transactionsWithSimulated, accounts);
}

function getDashboardProjectScopedDepositTransactions(transactions, simulatedTransactions, baseDate, projects) {
  const isolatedProjectIds = projects?.filter((project) => project.settings?.isIsolated).map((project) => project.id) || [];
  const projectSimulatedTransactions = (simulatedTransactions || [])
    .filter((txn) => !isolatedProjectIds.includes(txn.projectId))
    .filter((txn) => txn.date <= baseDate);

  return (transactions || []).concat(projectSimulatedTransactions).filter((txn) => txn.date <= baseDate);
}

function getDashboardProjectScopedLoanTransactions(simulatedTransactions, baseDate, projects) {
  const isolatedProjectIds = projects?.filter((project) => project.settings?.isIsolated).map((project) => project.id) || [];
  return (simulatedTransactions || [])
    .filter((txn) => !isolatedProjectIds.includes(txn.projectId))
    .filter((txn) => txn.date <= baseDate);
}

export const loanTransactions = createSelector(
  (state) => state.data?.deposits.transactions || [],
  (state) => state.data?.deposits.simulatedTransactions || [],
  (state) => state.data?.loans.simulatedTransactions || [],
  (state) => state.data?.loans.accounts || [],
  globals.globalQuotesView,
  (state) => state.simulation.dashboardMode,
  (state) => state.simulation.baseDate,
  (state) => state.user.profile.settings.baseCurrency,
  (state) => state.projects,
  (depositTransactions, depositSimulatedTransactions, explicitSimulatedLoanTransactions, accounts, quotes, dashboardMode, baseDate, baseCurrency, projects) => {
    if (dashboardMode === 'dashboard') {
      const realizedTransactions = deriveRealizedLoanTransactions(depositTransactions, accounts, baseCurrency, quotes);
      const transactionsWithFifo = accounts.flatMap((loanAccount) => applyFIFOReturnAll(
        realizedTransactions.filter((transaction) => transaction.accountId === loanAccount.id),
        loanAccount.direction === 'received',
      ));
      return applyLoanTransactionTypes(transactionsWithFifo, accounts);
    }

    return buildLoanTransactions({
      depositTransactions: getDashboardProjectScopedDepositTransactions(depositTransactions, depositSimulatedTransactions, baseDate, projects),
      explicitSimulatedLoanTransactions: getDashboardProjectScopedLoanTransactions(explicitSimulatedLoanTransactions, baseDate, projects),
      accounts,
      quotes,
      baseCurrency,
      baseDate,
    });
  },
);

export const loanTransactionsProjectView = createSelector(
  (state) => state.data?.deposits.transactions || [],
  (state) => state.data?.deposits.simulatedTransactions || [],
  (state) => state.data?.loans.simulatedTransactions || [],
  (state) => state.data?.loans.accounts || [],
  globals.globalQuotesView,
  (state) => state.user.profile.settings.baseCurrency,
  (depositTransactions, depositSimulatedTransactions, explicitSimulatedLoanTransactions, accounts, quotes, baseCurrency) => buildLoanTransactions({
    depositTransactions: depositTransactions.concat(depositSimulatedTransactions),
    explicitSimulatedLoanTransactions,
    accounts,
    quotes,
    baseCurrency,
  }),
);

function getConnectedDepositAccountId(loanAccounts, loanAccountId) {
  return loanAccounts.find((account) => account.id === loanAccountId)?.connectedDepositAccounts?.[0];
}

function createCanonicalDepositLoanRepayment(_loanTransaction, _loanAccounts, _depositAccounts, _quotes) {
  let depositAccount;
  try {
    const depositAccountId = getConnectedDepositAccountId(_loanAccounts, _loanTransaction.accountId);
    depositAccount = _depositAccounts.find((account) => account.id === depositAccountId);

    if (!depositAccount) {
      console.error('No connected deposit account found for loan account', _loanTransaction.accountId);
      return null;
    }

    const principalLoanCurrency = (-1) * _loanTransaction.quantity;
    const interestLoanCurrency = (-1) * _loanTransaction.quantityInterest;
    let principalAccountCurrency = principalLoanCurrency;
    let interestAccountCurrency = interestLoanCurrency;
    let accountCurrencyAmount = principalAccountCurrency + interestAccountCurrency;
    let fxAmount = null;
    let fxCurrency = null;
    let uptc = 1;

    if (depositAccount.currency !== _loanTransaction.accountCurrency) {
      const accountCurrencyQuote = _quotes?.[depositAccount.currency]?.[_loanTransaction.date]?.quoteBaseCurrency || _quotes?.[depositAccount.currency]?.current?.quoteBaseCurrency;
      const loanCurrencyQuote = _quotes?.[_loanTransaction.accountCurrency]?.[_loanTransaction.date]?.quoteBaseCurrency || _quotes?.[_loanTransaction.accountCurrency]?.current?.quoteBaseCurrency;

      if (accountCurrencyQuote && loanCurrencyQuote) {
        const conversionRate = loanCurrencyQuote / accountCurrencyQuote;
        principalAccountCurrency = principalLoanCurrency * conversionRate;
        interestAccountCurrency = interestLoanCurrency * conversionRate;
        accountCurrencyAmount = principalAccountCurrency + interestAccountCurrency;
        fxAmount = principalLoanCurrency + interestLoanCurrency;
        fxCurrency = _loanTransaction.accountCurrency;
        uptc = accountCurrencyAmount !== 0 ? fxAmount / accountCurrencyAmount : 1;
      }
    }

    const transaction = depositTransaction.cast({
      category: 'deposits',
      isSimulated: true,
      otherParty: 'Bank',
      id: _loanTransaction.tags?.linkedDepositTransctionId || buildDerivedId(_loanTransaction.id, 'deposit-loanRepayment'),
      date: _loanTransaction.date,
      description: _loanTransaction.description,
      accountId: depositAccountId,
      projectId: _loanTransaction.projectId,
      amount: accountCurrencyAmount * _loanTransaction.upbc,
      currency: depositAccount.currency,
      accountCurrencyAmount,
      quantity: accountCurrencyAmount,
      upbc: _loanTransaction.upbc,
      upac: 1,
      uptc,
      fxAmount,
      fxCurrency,
      label: 'investment-loanRepayment',
      linkedTransactions: [
        {
          id: _loanTransaction.id,
          category: 'loans',
          tags: {
            type: 'transfer',
          },
        },
      ],
      tags: {
        accountId: _loanTransaction.accountId,
        loanRepayment: {
          principal: Math.abs(principalAccountCurrency),
          interest: Math.abs(interestAccountCurrency),
        },
      },
    });

    return transaction;
  } catch (error) {
    console.error('simulatedLoanInstallmentView > transaction creation error for', _loanTransaction, error);
    console.info('simulatedLoanInstallmentView: depositAccount: ', depositAccount);
  }
  return null;
}

/**
 * This function takes all real and simulated loan transactions from loanTransactions and generates simulated deposit-side transactions for the loan transactions.
 * The input transactions are provided from depositsTransactions selector, which reads loanTransactions as input.
 */
export function simulatedLoanInstallmentView(_loanTransactions, _loanAccounts, _depositAccounts, _quotes) {
  if (_loanAccounts.length === 0 || _depositAccounts.length === 0 || _loanTransactions.length === 0) return [];

  return _loanTransactions
    .filter((transaction) => transaction.isSimulated)
    .map((transaction) => createCanonicalDepositLoanRepayment(transaction, _loanAccounts, _depositAccounts, _quotes))
    .filter(Boolean);
}
